import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { isNil } from "lodash-es";

import { type BlockType, PdfState } from "../../shared/types/worksheet";
import { logInfoWithDebugFlag } from "../../utils/logger";

import { STUDENT_VIEW_QUERY_PARAM, TIMELINE_VIEW_QUERY_PARAM } from "components/constants";
import { ViewportDimensions } from "components/materials/presentation/projector/projector-messaging";
import type { PageTabVariant } from "providers/DocViewProvider";
import { createReplaceAllReducer } from "store/utils";
import { getDefaultPdfState } from "utils/asset";

// TODO: Move all query param handling into the store
const initialQueryParams = new URLSearchParams(window.location.search);

// This is just for logging purposes, not to be used for a page refresh. This is used
// to track erroneous data that may be loaded into the UX when it shouldn't have been.
// It's only used by lowerSectionVersionTo at the moment, which is only called in
// pubsub-slice on API response data--if the section version returned differs by more
// than 50, there is a good chance that the API call was not filtered by the section.
const MAX_SECTION_VERSION_DIFF_FOR_LOGGING = 50;

const boolFromParam = (paramName: string) => {
  const value = initialQueryParams.get(paramName);
  return value === "1";
};

const replaceBoolQueryParam = (paramName: string, value: boolean) => {
  // This function specifically does not trigger a reload or history change
  const newParams = new URLSearchParams(window.location.search);
  if (value) {
    newParams.set(paramName, "1");
  } else {
    newParams.delete(paramName);
  }
  window.history.replaceState({}, "", `${window.location.pathname}?${newParams.toString()}`);
};

type ViewState = {
  isSectionVersionOutOfSync: boolean;
  sectionVersion: number;
  isStudentPreview: boolean;
  isTimeline: boolean;
  /**
   * True while we are forcing a worksheet refresh due to a permission/group change.
   * Used to prevent submitting ops while the refresh is in-flight.
   */
  worksheetIsRefreshing: boolean;
  /**
   * True while the section route loader's awaited path is pending (sections, section_users,
   * assessment statuses, class_users list/create). Fire-and-forget list() calls may still be
   * in progress when this becomes false.
   */
  criticalSectionDataPending: boolean;
  shouldShowModalOnExitAssessmentMode: boolean;
  shouldResetPageTab: boolean;
  isGroupConfigShown: boolean;
  selectedPageTabVariant: PageTabVariant | null;
  assetModalIsOpen: boolean;
  preventFeatureModalClosing: boolean;
  featuringPdfState: PdfState;
  currentSectionId: string | null;
  /** Set while the section page route is mounted (URL …/page/:pageId). */
  currentPageId: string | null;
  viewingScreenshare: boolean;
  // We have logic based on whether current section is set or not so we need a separate field for selecting a section
  // in the admin view.
  adminSectionId: string | null;
  sectionSettingsTabIndex: number | undefined;

  // For Page Block Table of Contents
  selectedPageBlockIndex: number | null;
  currentAvailablePageBlocks: { index: number; blockType: BlockType["t"] }[];

  // Websocket/Save status indicators
  lastOpAt: number | null;
  lastOpSavedAt: number | null;
  opError: string | null;
  connectionQuality: "poor" | "good" | "fair";
  userIsInactive: boolean;
  // Query params as a string, without the leading ?
  queryParams: string;
  /* projector dimensions */
  projectorHeight: number | null;
  projectorFeaturedAssetContainer:
    | (ViewportDimensions & {
        hasHorizontalScrollbar: boolean;
        hasVerticalScrollbar: boolean;
      })
    | null;
  /* stored collaboration server version for force reload mechanism */
  storedCollaborationServerVersion: string | null;
  /* flag to show force reload modal */
  showForceReloadModal: boolean;
  /* flag to show version mismatch warning banner */
  showVersionMismatchWarning: boolean;
};

const initialState = () =>
  ({
    sectionVersion: 0,
    currentSectionId: null,
    currentPageId: null,
    isSectionVersionOutOfSync: false,
    isStudentPreview: boolFromParam(STUDENT_VIEW_QUERY_PARAM),
    isTimeline: boolFromParam(TIMELINE_VIEW_QUERY_PARAM),
    isGroupConfigShown: false,
    selectedPageTabVariant: "my_draft",
    assetModalIsOpen: false,
    preventFeatureModalClosing: false,
    featuringPdfState: getDefaultPdfState(),
    currentAvailablePageBlocks: [],
    selectedPageBlockIndex: null,
    adminSectionId: null,
    worksheetIsRefreshing: false,
    criticalSectionDataPending: false,
    shouldShowModalOnExitAssessmentMode: false,
    shouldResetPageTab: false,
    viewingScreenshare: false,
    sectionSettingsTabIndex: undefined,
    lastOpAt: null,
    lastOpSavedAt: null,
    opError: null,
    connectionQuality: "good",
    userIsInactive: false,
    queryParams: new URLSearchParams(window.location.search).toString(),
    projectorHeight: null,
    projectorFeaturedAssetContainer: null,
    storedCollaborationServerVersion: null,
    showForceReloadModal: false,
    showVersionMismatchWarning: false,
  }) as ViewState;

export const ViewStore = createSlice({
  name: "view",
  initialState,
  reducers: {
    /**
     * If we are updating the section version number from the API (pubsub-slice), we want the
     * section version to be set to the lowest section version received. This is because we don't
     * know what messages we may miss in between loading the data on the page and pubsub messages
     * in between, so we pull the few messages we may miss in this transition period. Nothing is
     * set if the new section version is undefined. */
    lowerSectionVersionTo: (
      state,
      action: PayloadAction<{
        results: { section_version?: number; section_id?: string }[] | undefined;
        modelName: string;
        params?: Record<string, string>;
      }>,
    ) => {
      const { results, modelName, params } = action.payload;
      const sectionId = state.currentSectionId;

      const result = results ? results.find((res) => res.section_id === sectionId) : undefined;
      if (!result) {
        if (sectionId) {
          logInfoWithDebugFlag(
            "debug_pubsub",
            "Why are we returning data for a different section?",
            {
              result,
              currentSectionId: sectionId,
              modelName,
              params,
            },
          );
        }

        return;
      }

      const newSectionVersion = result?.section_version;
      const oldSectionVersion = state.sectionVersion;
      if (!isNil(newSectionVersion) && newSectionVersion < oldSectionVersion) {
        const versionDiff = Math.abs(newSectionVersion - oldSectionVersion);
        if (versionDiff >= MAX_SECTION_VERSION_DIFF_FOR_LOGGING) {
          logInfoWithDebugFlag(
            "debug_pubsub",
            `Lowering section version, version diff: ${versionDiff}`,
            {
              modelName,
              currentSectionId: sectionId,
              newSectionVersion,
              oldSectionVersion,
            },
          );
        }

        state.sectionVersion = newSectionVersion;
      }
    },
    /** Whenever we switch sections, we set the section version to the section's section_version.
     * This may do nothing because we already load the section version when we load data from
     * the API. Nothing is set if the new section version is undefined. */
    setSectionVersionTo: (state, action: PayloadAction<number | undefined>) => {
      const newSectionVersion = action.payload;
      if (!isNil(newSectionVersion)) {
        state.sectionVersion = newSectionVersion;
      }
    },
    setIsSectionVersionOutOfSync: (state, action: PayloadAction<boolean>) => {
      state.isSectionVersionOutOfSync = action.payload;
    },
    setShouldShowModalOnExitAssessmentMode: (state, action: PayloadAction<boolean>) => {
      state.shouldShowModalOnExitAssessmentMode = action.payload;
    },
    setShouldResetPageTab: (state, action: PayloadAction<boolean>) => {
      state.shouldResetPageTab = action.payload;
    },
    setAssetModalIsOpen: (state, action: PayloadAction<boolean>) => {
      state.assetModalIsOpen = action.payload;
    },
    setPreventFeatureModalClosing: (state, action: PayloadAction<boolean>) => {
      state.preventFeatureModalClosing = action.payload;
    },
    clearFeaturingPdfState: (state) => {
      state.featuringPdfState = getDefaultPdfState();
    },
    setFeaturingPdfState: (state, action: PayloadAction<Partial<PdfState>>) => {
      state.featuringPdfState = { ...state.featuringPdfState, ...action.payload };
    },
    setSelectedPageTabVariant: (state, action: PayloadAction<PageTabVariant | null>) => {
      state.selectedPageTabVariant = action.payload;
    },
    setIsStudentPreview: (state, action: PayloadAction<boolean>) => {
      state.isStudentPreview = action.payload;
      replaceBoolQueryParam(STUDENT_VIEW_QUERY_PARAM, action.payload);
    },
    setIsTimelineView: (state, action: PayloadAction<boolean>) => {
      state.isTimeline = action.payload;
      replaceBoolQueryParam(TIMELINE_VIEW_QUERY_PARAM, action.payload);
    },
    onSendShareDBOp: (state) => {
      state.lastOpAt = new Date().getTime();
    },
    onReceiveShareDBOp: (state) => {
      state.lastOpSavedAt = new Date().getTime();
      state.opError = null;
    },
    onShareDBError: (state, action: PayloadAction<string>) => {
      state.opError = action.payload;
    },
    setCurrentSectionId: (state, action: PayloadAction<string | null>) => {
      state.currentSectionId = action.payload;
    },
    setCurrentPageId: (state, action: PayloadAction<string | null>) => {
      state.currentPageId = action.payload;
    },
    setShowGroupConfig: (state, action: PayloadAction<boolean>) => {
      state.isGroupConfigShown = action.payload;
    },
    setWorksheetIsRefreshing: (state, action: PayloadAction<boolean>) => {
      state.worksheetIsRefreshing = action.payload;
    },
    setCriticalSectionDataPending: (state, action: PayloadAction<boolean>) => {
      state.criticalSectionDataPending = action.payload;
    },
    // Don't use this directly, use the useQueryParamsManager hook instead.
    updateQueryParams: (state, action: PayloadAction<string>) => {
      state.queryParams = action.payload;
    },
    setViewingScreenshare: (state, action: PayloadAction<boolean>) => {
      state.viewingScreenshare = action.payload;
    },
    setSelectedPageBlockIndex: (state, action: PayloadAction<number | null>) => {
      state.selectedPageBlockIndex = action.payload;
    },
    setCurrentAvailablePageBlocks: (
      state,
      action: PayloadAction<{ index: number; blockType: BlockType["t"] }[]>,
    ) => {
      state.currentAvailablePageBlocks = action.payload;
    },
    setSectionSettingsForSectionId: (state, action: PayloadAction<string | null>) => {
      state.adminSectionId = action.payload;
      state.sectionSettingsTabIndex = action.payload !== null ? 1 : undefined; // Default to Course Info tab
    },
    setSectionSettingsTabIndex: (state, action: PayloadAction<number | undefined>) => {
      state.sectionSettingsTabIndex = action.payload;
      if (action.payload === undefined) {
        state.adminSectionId = null;
      }
    },
    setConnectionQuality: (state, action: PayloadAction<"poor" | "good" | "fair">) => {
      state.connectionQuality = action.payload;
    },
    setUserIsInactive: (state, action: PayloadAction<boolean>) => {
      state.userIsInactive = action.payload;
    },
    setProjectorHeight: (state, action: PayloadAction<number | null>) => {
      state.projectorHeight = action.payload;
    },
    setProjectorFeaturedAssetContainer: (
      state,
      action: PayloadAction<
        | (ViewportDimensions & {
            hasHorizontalScrollbar: boolean;
            hasVerticalScrollbar: boolean;
          })
        | null
      >,
    ) => {
      state.projectorFeaturedAssetContainer = action.payload;
    },
    setStoredCollaborationServerVersion: (state, action: PayloadAction<string | null>) => {
      state.storedCollaborationServerVersion = action.payload;
    },
    setShowForceReloadModal: (state, action: PayloadAction<boolean>) => {
      state.showForceReloadModal = action.payload;
    },
    setShowVersionMismatchWarning: (state, action: PayloadAction<boolean>) => {
      state.showVersionMismatchWarning = action.payload;
    },
  },
  extraReducers: createReplaceAllReducer<ViewState>("view", initialState),
});

export const viewStateActions = ViewStore.actions;
